/**
 * Cell renderers and toolbar shared by the two nested saved-view renderings
 * (#1144): NestedBillTable (mockup 1a) and NestedBillCards (mockup 1c).
 * One implementation so table and card cells cannot drift.
 *
 * Mockup hex values are mapped to theme tokens; no report coupling (§2.1).
 */

import React from 'react';
import Button from '../Button';
import Chip from '../Chip';
import type { Bill } from '../../types/bill';
import type { BillWorkspaceRecord, SavedViewColumnEntry } from '../../types/savedViews';
import {
  WORKSPACE_COL_POSITION,
  WORKSPACE_COL_NOTES,
  customFieldIdFromColId,
} from '../../types/savedViews';
import { getCommitteeNameShort } from '../../utils/committeeUtils';
import { getPositionColorClasses, getPositionLabel } from '../../utils/positionColors';
import {
  PositionEditCell,
  CustomFieldEditCell,
  NotesEditCell,
  type RecordEditing,
} from './WorkspaceRecordEditors';

export type { RecordEditing, RecordEditHandlers } from './WorkspaceRecordEditors';
import { formatDate } from '../../utils/dateUtils';
import toast from 'react-hot-toast';
import { Bookmark, Share2, Vote } from 'lucide-react';
import { BILL_ACTIONS } from './billColumnCatalog';
import type { BillActionKey } from '../../types/savedViews';

export interface WorkspaceColumnLabels {
  /** field_definition_id → display name (from custom_field_definitions). */
  customFieldNames: Record<string, string>;
  /** field_definition_id → description, shown as a hover on the header label. */
  customFieldDescriptions?: Record<string, string>;
}

/** Handlers for the bill-level Actions column (QA round 10). */
export interface BillActionsConfig {
  keys: BillActionKey[];
  onBookmark?: (billId: string, billNumber: string, billTitle: string) => void;
  onVotePredictions?: (bill: Bill) => void;
  /** Tints the bookmark when the bill is already tracked. */
  trackedBillIds?: Set<string>;
}

export const dash = (
  <span className="text-text-tertiary dark:text-text-dark-tertiary">—</span>
);

export function dateCell(value?: string | null): React.ReactNode {
  if (!value) return dash;
  const formatted = formatDate(value);
  return formatted && formatted !== '-' ? formatted : dash;
}

/** "Velasco, Elizabeth (D)" → "Velasco". */
export function surname(name: string): string {
  const comma = name.indexOf(',');
  return comma > 0 ? name.slice(0, comma) : name.split(' ')[0];
}

/** Two-letter monogram for the workspace avatar. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? '')).toUpperCase() || '?';
}

function statusTone(status: string): 'positive' | 'negative' | 'neutral' {
  if (/signed|became law/i.test(status)) return 'positive';
  if (/postpone|fail|lost|dead/i.test(status)) return 'negative';
  return 'neutral';
}

export function workspaceColumnLabel(colId: string, labels: WorkspaceColumnLabels): string {
  if (colId === WORKSPACE_COL_POSITION) return 'Position';
  if (colId === WORKSPACE_COL_NOTES) return 'Notes';
  const fieldId = customFieldIdFromColId(colId);
  return (fieldId && labels.customFieldNames[fieldId]) || 'Custom field';
}

/** Description for the header hover (custom fields only); undefined otherwise. */
export function workspaceColumnDescription(
  colId: string,
  labels: WorkspaceColumnLabels
): string | undefined {
  const fieldId = customFieldIdFromColId(colId);
  return (fieldId && labels.customFieldDescriptions?.[fieldId]) || undefined;
}

/** Status pill + date (both mockups' right-hand treatment). */
export const BillStatusCell: React.FC<{ bill: Bill; alignEnd?: boolean }> = ({ bill, alignEnd }) => {
  const status = bill.status || '';
  if (!status) return dash;
  const short = status.length <= 30;
  return (
    <span className={`min-w-0 flex flex-col gap-1 ${alignEnd ? 'items-end text-right' : 'items-start'}`}>
      {short ? (
        <Chip label={status} tone={statusTone(status)} size="sm" />
      ) : (
        <span className="block break-words text-sm">{status}</span>
      )}
      {bill.lastActivity && (
        <span className="text-[11px] text-text-tertiary dark:text-text-dark-tertiary tabular-nums">
          {dateCell(bill.lastActivity)}
        </span>
      )}
    </span>
  );
};

export const SubjectChips: React.FC<{ bill: Bill }> = ({ bill }) => {
  const subjects = Array.isArray(bill.subject) ? bill.subject : [bill.subject].filter(Boolean);
  if (!subjects.length) return dash;
  return (
    <span className="flex flex-wrap gap-1.5">
      {subjects.map((s) => (
        <Chip key={s} label={s} tone="brand" size="sm" />
      ))}
    </span>
  );
};

/** Sponsors as surnames: "Velasco, McCluskie +2 more" (table) or "Velasco · McCluskie · …" (cards). */
export const SponsorNames: React.FC<{ bill: Bill; mode: 'plus' | 'dots' }> = ({ bill, mode }) => {
  const names = (bill.sponsors ?? [])
    .map((s: { name?: string }) => s.name)
    .filter((n): n is string => !!n);
  if (!names.length) return dash;
  if (mode === 'dots') {
    const shown = names.slice(0, 4).map(surname);
    const more = names.length - shown.length;
    return (
      <span className="break-words text-[13px] text-text-secondary dark:text-text-dark-secondary">
        {shown.join(' · ')}
        {more > 0 && <span className="text-text-tertiary dark:text-text-dark-tertiary"> +{more} more</span>}
      </span>
    );
  }
  const lead = names.slice(0, 2).map(surname).join(', ');
  const more = names.length - 2;
  return (
    <span className="break-words">
      <span className="text-text-secondary dark:text-text-dark-secondary">{lead}</span>
      {more > 0 && <span className="text-text-tertiary dark:text-text-dark-tertiary"> +{more} more</span>}
    </span>
  );
};

const ACTION_TITLES = Object.fromEntries(BILL_ACTIONS.map((a) => [a.key, a.description]));

/** The Actions cell: bookmark / share / vote predictions, per the view's selection. */
export const BillActionsCell: React.FC<{ bill: Bill; config: BillActionsConfig }> = ({
  bill,
  config,
}) => {
  const shareBill = () => {
    const publicUrl = `${window.location.origin}/public/bills/${encodeURIComponent(bill.billNumber)}`;
    navigator.clipboard
      .writeText(publicUrl)
      .then(() => toast.success('Public link copied to clipboard'))
      .catch(() => toast.error('Failed to copy the link'));
  };
  const isTracked = config.trackedBillIds?.has(bill.id) ?? false;
  const buttonClass =
    'p-1 rounded-md text-text-tertiary dark:text-text-dark-tertiary hover:text-primary-700 dark:hover:text-primary-300 transition-colors';

  return (
    <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      {config.keys.includes('bookmark') && config.onBookmark && (
        <button
          type="button"
          title={ACTION_TITLES.bookmark}
          aria-label={`${ACTION_TITLES.bookmark}: ${bill.billNumber}`}
          onClick={() => config.onBookmark?.(bill.id, bill.billNumber, bill.title)}
          className={`${buttonClass} ${isTracked ? 'text-primary-700 dark:text-primary-300' : ''}`}
        >
          <Bookmark className={`w-4 h-4 ${isTracked ? 'fill-current' : ''}`} />
        </button>
      )}
      {config.keys.includes('share') && (
        <button
          type="button"
          title={ACTION_TITLES.share}
          aria-label={`${ACTION_TITLES.share}: ${bill.billNumber}`}
          onClick={shareBill}
          className={buttonClass}
        >
          <Share2 className="w-4 h-4" />
        </button>
      )}
      {config.keys.includes('votePredictions') && config.onVotePredictions && (
        <button
          type="button"
          title={ACTION_TITLES.votePredictions}
          aria-label={`${ACTION_TITLES.votePredictions}: ${bill.billNumber}`}
          onClick={() => config.onVotePredictions?.(bill)}
          className={buttonClass}
        >
          <Vote className="w-4 h-4" />
        </button>
      )}
    </span>
  );
};

/** Generic bill-level cell for the remaining catalog columns. */
export function billValueCell(
  bill: Bill,
  colId: string,
  billActions?: BillActionsConfig
): React.ReactNode {
  switch (colId) {
    case 'actions':
      return billActions ? <BillActionsCell bill={bill} config={billActions} /> : dash;
    case 'subjects':
      return <SubjectChips bill={bill} />;
    case 'status':
      return <BillStatusCell bill={bill} />;
    case 'sponsors':
      return <SponsorNames bill={bill} mode="plus" />;
    case 'introducedDate':
      return dateCell(bill.introducedDate);
    case 'calendarDate':
      return bill.nextCalendarEvent ? dateCell(bill.nextCalendarEvent.reading_date) : dash;
    case 'lastAmendment':
      return bill.lastAmendment
        ? `${formatDate(bill.lastAmendment.date)}${bill.lastAmendment.count ? ` (${bill.lastAmendment.count})` : ''}`
        : 'No amendments';
    case 'houseCommittees':
    case 'senateCommittees': {
      const chamber = colId === 'houseCommittees' ? 'house' : 'senate';
      const names = (bill.committees ?? [])
        .filter((c: { chamber?: string }) => c.chamber?.toLowerCase() === chamber)
        .map((c: { name: string }) => getCommitteeNameShort(c.name));
      return names.length ? <span className="block break-words">{names.join(', ')}</span> : dash;
    }
    default:
      return dash;
  }
}

/** Workspace avatar + name (both mockups). */
export const WorkspaceName: React.FC<{ name: string }> = ({ name }) => (
  <span className="min-w-0 flex items-center gap-2">
    <span className="flex h-5 w-5 flex-none items-center justify-center rounded-md bg-primary-100 dark:bg-primary-900/40 text-[9px] font-bold text-primary-700 dark:text-primary-300">
      {initials(name)}
    </span>
    <span className="break-words text-[13px] font-semibold text-text-primary dark:text-text-dark-primary">
      {name}
    </span>
  </span>
);

/** One workspace-level value cell: position pill, notes link, or custom field value. */
export function workspaceValueCell(
  bill: Bill,
  record: BillWorkspaceRecord,
  col: SavedViewColumnEntry,
  onOpenNotes?: (bill: Bill, record: BillWorkspaceRecord) => void,
  editing?: RecordEditing
): React.ReactNode {
  if (col.colId === WORKSPACE_COL_POSITION) {
    if (editing) {
      return (
        <PositionEditCell bill={bill} record={record} onChange={editing.handlers.onPositionChange} />
      );
    }
    const position = record.position || 'Pending';
    return (
      <span
        className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${getPositionColorClasses(position)}`}
      >
        {getPositionLabel(position)}
      </span>
    );
  }
  if (col.colId === WORKSPACE_COL_NOTES) {
    const last = record.lastNoteAt ? formatDate(record.lastNoteAt) : null;
    const summary = !record.notesCount ? (
      dash
    ) : (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onOpenNotes?.(bill, record);
        }}
        className="text-primary-600 dark:text-primary-400 hover:underline text-left break-words text-[13px]"
      >
        {record.notesCount} {record.notesCount === 1 ? 'note' : 'notes'}
        {last ? ` · last ${last}` : ''}
      </button>
    );
    if (editing) {
      return (
        <NotesEditCell bill={bill} record={record} summary={summary} onAddNote={editing.handlers.onAddNote} />
      );
    }
    return summary;
  }
  const fieldId = customFieldIdFromColId(col.colId);
  if (fieldId && editing) {
    const field = editing.defsById[fieldId];
    if (field) {
      return (
        <CustomFieldEditCell
          bill={bill}
          record={record}
          field={field}
          onSave={editing.handlers.onCustomFieldSave}
        />
      );
    }
  }
  const value = fieldId ? record.customFieldValues?.[fieldId] : undefined;
  if (value === null || value === undefined || value === '') return dash;
  return (
    <span className="block break-words text-[13px]">
      {Array.isArray(value) ? value.join(', ') : String(value)}
    </span>
  );
}

export interface NestedViewToolbarProps {
  /** Tracked/All toggle (R7a in the toolbar). Absent handler = static title (Bill Tracking tab). */
  trackedMode?: boolean;
  onTrackedModeChange?: (tracked: boolean) => void;
  /** Expand/collapse-all — table rendering only; cards are always expanded. */
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
}

/** Toolbar shared by nested table and card renderings. */
export const NestedViewToolbar: React.FC<NestedViewToolbarProps> = ({
  trackedMode,
  onTrackedModeChange,
  onExpandAll,
  onCollapseAll,
}) => (
  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-border-light dark:border-border-dark bg-white dark:bg-background-dark-secondary rounded-t-lg">
    <div className="flex items-center gap-3 min-w-0">
      {onTrackedModeChange ? (
        <div
          role="group"
          aria-label="Which bills to show"
          className="inline-flex rounded-lg border border-border-light-secondary dark:border-border-dark p-0.5"
        >
          {([
            { tracked: true, label: 'Tracked bills' },
            { tracked: false, label: 'All bills' },
          ] as const).map(({ tracked, label }) => (
            <button
              key={label}
              type="button"
              aria-pressed={trackedMode === tracked}
              onClick={() => onTrackedModeChange(tracked)}
              className={`rounded-md px-3 py-1 text-xs font-semibold transition-colors ${
                trackedMode === tracked
                  ? 'bg-primary-700 text-white'
                  : 'text-text-secondary dark:text-text-dark-secondary hover:bg-primary-50 dark:hover:bg-background-dark-tertiary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      ) : (
        <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
          Tracked bills
        </h3>
      )}
    </div>
    {(onExpandAll || onCollapseAll) && (
      <div className="flex items-center gap-2">
        {onExpandAll && (
          <Button variant="outline" size="sm" onClick={onExpandAll} noShadow>
            Expand all
          </Button>
        )}
        {onCollapseAll && (
          <Button variant="outline" size="sm" onClick={onCollapseAll} noShadow>
            Collapse all
          </Button>
        )}
      </div>
    )}
  </div>
);
